import React, { useCallback, useState } from "react";
import { Location, useLocation } from "react-router-dom";
import { Formik, FormikConfig } from "formik";
import { Extends } from "@common/helpers/typescript";
import {
    Entity,
    EntityProfile,
    EntityProfileRevenueBracket,
    EntityProfileType,
} from "@common/types/entity";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { Workspace } from "@common/types/workspace";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { Button } from "@design-system/Button/Button";
import {
    useEditEntityMutation,
    useEntityCreationMutation,
} from "@api/entity.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import {
    EntityProfileFormSchema,
    entityProfileFormSchema,
} from "@components/onboarding/useEntityProfileFormConfig";
import { StateAwareNavLink } from "@components/routes/StateAwareNavLink";
import { CloseIcon } from "@icons";
import { OnboardingStepKey } from "../steps";
import { useManageSteps } from "../useManageSteps";
import { BaseOnboardingContainer } from "../components/BaseOnboardingContainer";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import { WorkspacePreview } from "../components/WorkspacePreview/WorkspacePreview";
import { ConnectAccounts } from "../steps/ConnectAccounts/ConnectAccounts";
import { CreateEntityProfile } from "../steps/CreateEntityProfile";
import { ConnectAccountsSidebar } from "../steps/ConnectAccounts/ConnectAccountsSidebar";
import containerStyles from "../components/BaseOnboardingContainer.module.scss";
import { DEFAULT_COUNTRY_CODE } from "../constants";

type NewEntityFlowSteps = Extends<
    OnboardingStepKey,
    "profile" | "connectAccounts"
>;

// The caller (e.g. the ownership canvas) can request where to return after the
// flow via location state; otherwise the accounts page is the default landing.
const DEFAULT_RETURN_PATH = "/accounts";

interface NewEntityLocationState {
    returnTo?: string;
}

export const NewEntity: React.FC = () => {
    const [profileFormBusy, setProfileFormBusy] = useState(false);
    const navigate = useStateAwareNavigate();
    const { state }: Location<NewEntityLocationState | null> = useLocation();
    const returnPath = state?.returnTo ?? DEFAULT_RETURN_PATH;
    const { go, currentStep, nextStep, isExiting } =
        useManageSteps<NewEntityFlowSteps>(true, () => "profile");

    const [entityToCreate, setEntityToCreate] = useState<Partial<Entity>>({});
    const activeWorkspace = useActiveWorkspace();

    const createEntity = useEntityCreationMutation(
        activeWorkspace as Workspace,
    );
    const editEntity = useEditEntityMutation(entityToCreate as Entity);

    const onFinished = useCallback(() => {
        navigate(returnPath);
    }, [navigate, returnPath]);

    const handleNewEntityProfile = useCallback(
        async (
            name: string,
            legalType: EntityProfileType,
            profile: EntityProfile,
        ) => {
            let createdEntity: Entity;

            if (entityToCreate.id) {
                createdEntity = await editEntity.mutateAsync({
                    name,
                    legalType,
                    profile: {
                        ...entityToCreate.profile,
                        ...profile,
                    },
                });
            } else {
                createdEntity = await createEntity.mutateAsync({
                    name,
                    profile,
                    legalType,
                });
            }

            setEntityToCreate(createdEntity);

            go("connectAccounts");
        },
        [
            createEntity,
            editEntity,
            entityToCreate.id,
            entityToCreate.profile,
            go,
        ],
    );

    const form: FormikConfig<EntityProfileFormSchema> = {
        initialValues: {
            name: entityToCreate.name ?? "",
            legalType:
                entityToCreate.legalType ?? EntityProfileType.NO_LEGAL_ENTITY,
            industries: entityToCreate.profile?.industries ?? [],
            industryOther: entityToCreate.profile?.industryOther,
            annualRevenue:
                entityToCreate.profile?.annualRevenue ??
                EntityProfileRevenueBracket.LESS_THAN_50K,
            country: entityToCreate.profile?.country ?? DEFAULT_COUNTRY_CODE,
        },
        validate: validateFormikWithZodSchema(entityProfileFormSchema),
        onSubmit: submitHelper({
            loading: profileFormBusy,
            setLoading: setProfileFormBusy,
            handler: async ({
                legalType,
                name,
                country,
                industries,
                industryOther,
                annualRevenue,
            }) => {
                await handleNewEntityProfile(name, legalType, {
                    industries,
                    industryOther,
                    annualRevenue,
                    country,
                });
            },
        }),
    };

    let main: React.ReactNode;
    let sidebar: React.ReactNode;

    if (currentStep === "profile") {
        main = <CreateEntityProfile busy={profileFormBusy} />;
        sidebar = <WorkspacePreview />;
    } else {
        main = (
            <ConnectAccounts
                onBack={() => go("profile")}
                onFinished={onFinished}
                defaultEntity={entityToCreate as Entity}
                allowNoAccounts
            />
        );

        sidebar = <ConnectAccountsSidebar />;
    }

    return (
        <BaseOnboardingContainer>
            <Formik {...form}>
                <OnboardingLayout
                    currentStep={currentStep}
                    main={main}
                    nextStep={nextStep}
                    isExiting={isExiting}
                    sidebar={sidebar}
                />
            </Formik>

            <StateAwareNavLink
                to={returnPath}
                className={containerStyles.close}
            >
                <Button
                    iconOnly
                    variant="secondary"
                    size="md"
                    aria-label="Close"
                >
                    <CloseIcon />
                </Button>
            </StateAwareNavLink>
        </BaseOnboardingContainer>
    );
};
